/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Grid } from "@mui/material";
import {
  Checklist,
  Description,
  QuestionAnswer,
  Quiz,
  YouTube,
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import {
  ActivityType,
  LearnerRecord,
  Lesson,
  LessonDisplayType,
  LessonType,
  Verb,
} from "@ictlearningsciences/pal3-core/types";

import { StartButton } from "./buttons";
import { CardComponent } from "./card";
import LessonCompletionBar from "./lessoncompletionbar";
import { EditUserReflection, EditUserTest } from "./self-test";
import { useAppSelector } from "../store/hooks";

const useStyles = makeStyles()(() => ({
  cardRow: {
    alignItems: "center",
  },
  iconComp: {
    verticalAlign: "middle",
    fontWeight: 700,
    fontSize: "12px",
    display: "flex",
  },
  icon: {
    width: 15,
    height: "auto",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
  },
}));

export function LessonIcon(props: {
  type: LessonDisplayType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: Record<string, any>;
}): JSX.Element {
  const { type, style } = props;
  return type === LessonDisplayType.VIDEO ? (
    <YouTube style={style} data-cy="icon-video" />
  ) : type === LessonDisplayType.MULTIPLE_CHOICE ? (
    <Quiz style={style} data-cy="icon-mcq" />
  ) : type === LessonDisplayType.OPENTUTOR ? (
    <QuestionAnswer style={style} data-cy="icon-opentutor" />
  ) : type === LessonDisplayType.PLAN_DIALOGUE ? (
    <Checklist style={style} data-cy="icon-plan" />
  ) : (
    <Description style={style} data-cy="icon-text" />
  );
}

export const LessonResourceType = (props: {
  type: LessonDisplayType;
}): JSX.Element => {
  const { classes } = useStyles();
  return (
    <Grid
      data-cy="lesson-card-resource-type"
      container
      spacing={1}
      className={classes.cardRow}
    >
      <Grid
        item
        xs="auto"
        className={classes.iconComp}
        style={{ marginLeft: 5, marginRight: 5 }}
      >
        <LessonIcon type={props.type} />
      </Grid>
      <Grid item xs="auto" className={classes.iconComp}>
        {props.type === LessonDisplayType.MULTIPLE_CHOICE
          ? "mcq"
          : props.type?.includes("-")
          ? props.type?.split("-")[0]
          : props.type}
      </Grid>
    </Grid>
  );
};

export const LessonCompletionEstimate = (props: {
  low: number;
  high: number;
}): JSX.Element => {
  const { classes } = useStyles();

  return (
    <Grid
      data-cy="lesson-card-time-estimate"
      container
      spacing={1}
      className={classes.cardRow}
    >
      <Grid item xs="auto" className={classes.iconComp}>
        <FontAwesomeIcon icon={faClock} />
      </Grid>
      <Grid item xs="auto" className={classes.iconComp}>
        {props.low}-{props.high} minutes
      </Grid>
    </Grid>
  );
};

export const LessonXPIcon = (props: { points: number }): JSX.Element => {
  const { classes } = useStyles();
  return (
    <Grid
      data-cy="lesson-card-xp"
      container
      spacing={1}
      className={classes.cardRow}
    >
      <Grid item xs="auto" className={classes.iconComp}>
        <svg
          width="30"
          height="30"
          viewBox="0 0 30 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="30" height="30" rx="5" fill="#4E8F47" />
          <path
            d="M9.71481 11.2727L11.4748 14.2472H11.5429L13.3114 11.2727H15.3952L12.7319 15.6364L15.4549 20H13.3327L11.5429 17.0213H11.4748L9.68498 20H7.57134L10.3029 15.6364L7.62248 11.2727H9.71481ZM16.5628 20V11.2727H20.006C20.6679 11.2727 21.2319 11.3991 21.6978 11.652C22.1637 11.902 22.5188 12.25 22.7631 12.696C23.0103 13.1392 23.1338 13.6506 23.1338 14.2301C23.1338 14.8097 23.0088 15.321 22.7588 15.7642C22.5088 16.2074 22.1466 16.5526 21.6722 16.7997C21.2006 17.0469 20.6296 17.1705 19.9591 17.1705H17.7645V15.6918H19.6608C20.0159 15.6918 20.3086 15.6307 20.5387 15.5085C20.7716 15.3835 20.9449 15.2116 21.0586 14.9929C21.175 14.7713 21.2333 14.517 21.2333 14.2301C21.2333 13.9403 21.175 13.6875 21.0586 13.4716C20.9449 13.2528 20.7716 13.0838 20.5387 12.9645C20.3057 12.8423 20.0103 12.7812 19.6523 12.7812H18.408V20H16.5628Z"
            fill="white"
          />
        </svg>
      </Grid>
      <Grid item xs="auto" className={classes.iconComp}>
        +{props.points}
      </Grid>
    </Grid>
  );
};

export const LessonMasteryIcon = (props: { points: number }): JSX.Element => {
  const { classes } = useStyles();
  return (
    <Grid
      data-cy="lesson-card-mastery"
      container
      spacing={1}
      className={classes.cardRow}
    >
      <Grid item xs="auto" className={classes.iconComp}>
        <svg
          width="30"
          height="30"
          viewBox="0 0 30 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="30" height="30" rx="5" fill="#3984A0" />
          <path
            d="M10.6683 11.2727H12.9439L15.3473 17.1364H15.4495L17.8529 11.2727H20.1285V20H18.3387V14.3196H18.2663L16.0078 19.9574H14.789L12.5305 14.2983H12.4581V20H10.6683V11.2727Z"
            fill="white"
          />
        </svg>
      </Grid>
      <Grid item xs="auto" className={classes.iconComp}>
        +{props.points}
      </Grid>
    </Grid>
  );
};

export function LessonCard(props: { lesson: Lesson }): JSX.Element {
  const { lesson } = props;
  const { records } = useAppSelector((state) => state.userData);
  const [lastCompleted, setLastCompleted] = React.useState<LearnerRecord>();
  const [lastAttempted, setLastAttempted] = React.useState<LearnerRecord>();

  React.useEffect(() => {
    setLastCompleted(
      records.find((r) => {
        return (
          r.objType === ActivityType.LESSON &&
          r.objId === lesson.id &&
          r.verb === Verb.COMPLETED
        );
      })
    );
    setLastAttempted(
      records.find((r) => {
        return (
          r.objType === ActivityType.LESSON &&
          r.objId === lesson.id &&
          r.verb === Verb.ATTEMPTED
        );
      })
    );
  }, [lesson, records]);

  return (
    <CardComponent name="lesson">
      <Grid container spacing={0} justifyContent="space-between">
        <Grid xs={8}>
          <div className="card-title" data-cy="lesson-card-title">
            {lesson.name}
          </div>
        </Grid>
      </Grid>
      <div className="card-text" data-cy="lesson-card-description">
        {lesson.desc}
      </div>
      <Grid container spacing={2} className="card-row">
        {/* TODO: implement xp and mastery gain calculation */}
        {/* <Grid item xs="auto" className="icon-text start-button card-row">
          <LessonMasteryIcon points={0} />
        </Grid> */}
        {/* <Grid item xs="auto" className="icon-text start-button card-row">
          <LessonXPIcon points={0} />
        </Grid> */}
        <Grid item xs="auto" className="icon-text start-button">
          <LessonResourceType type={lesson.displayType} />
        </Grid>
        <Grid item xs="auto" className="icon-text start-button">
          <LessonCompletionEstimate
            low={lesson.estMinLow}
            high={lesson.estMinHigh}
          />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="space-between"
        className="card-row card-actions"
      >
        <Grid item xs="auto">
          <Box
            className="card-item"
            component="div"
            sx={{ display: "flex", alignItems: "center" }}
          >
            {lesson.type === LessonType.SCORED ? (
              <LessonCompletionBar
                percentage={lastCompleted?.result?.score || 0}
              />
            ) : (
              <LessonIcon
                type={lesson.displayType}
                style={{ color: lastAttempted ? "orange" : "#808080" }}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs="auto">
          <Box
            className="card-item"
            component="div"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <EditUserReflection lesson={lesson} small />
            <EditUserTest lesson={lesson} small />
            <StartButton lesson={lesson.id} />
          </Box>
        </Grid>
      </Grid>
    </CardComponent>
  );
}
